import Avo from '@susu/custom-made/Avo';
import useScrollUp from '@susu/custom-made/hooks/useScrollUp';
import { ThemeContext } from '@susu/custom-made/providers';
import { Clients, Level } from '@susu/custom-made/utils/constants';
import { imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type { MonogramPositions, Option } from '@susu/custom-made/utils/interfaces';
import { changeLevel } from '@susu/custom-made/utils/navigation';
import { getAvoEventParamsWithOption, getImageUrl, onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { SyntheticEvent, useContext } from 'react';

import Button, { ButtonSize, ButtonType } from '../../button/button';
import Modal from '../../modal/modal';
import Panel, { PanelSize } from '../../panel/panel';
import styles from './monogramDefaultPanel.module.scss';

export const MonogramDefaultPanel = ({
  monogramData,
  isMonogramShown,
  isDeleteMonogramModalOpen,
  deleteMonogramItem,
  editMonogram,
  deleteMonogram,
  closeDeleteMonogramModal,
  openMonogramDetails,
  confirmDeleteMonogram,
}: {
  monogramData: Option | undefined;
  isMonogramShown: boolean;
  isDeleteMonogramModalOpen: boolean;
  deleteMonogramItem: MonogramPositions | null;
  editMonogram: (positionI: MonogramPositions) => void;
  deleteMonogram: (position: MonogramPositions) => void;
  closeDeleteMonogramModal: () => void;
  openMonogramDetails: (positionId: number) => void;
  confirmDeleteMonogram: () => void;
}) => {
  const { dispatch, state } = useContext(ThemeContext);
  const scrollUp = useScrollUp();

  const confirmMonogram = () => {
    changeLevel(Level.Group, dispatch);
    state.client === Clients.onLine && Avo.basicOptionConfirm(getAvoEventParamsWithOption(state, 'Monogram'));
  };

  const onImageFailed = (e: SyntheticEvent<HTMLImageElement, Event>, fallbackImageUrl: string) => {
    if (fallbackImageUrl && e.currentTarget.src !== fallbackImageUrl) {
      e.currentTarget.src = fallbackImageUrl;
    }
  };

  return (
    <>
      <Panel isActive={isMonogramShown} closeClick={confirmMonogram} panelSize={PanelSize.Content} zIndex={21}>
        {isMonogramShown && (
          <div
            className={cn(styles.monogramContainer, {
              [styles.inStore]: state.client === Clients.inStore,
            })}
          >
            <div className={styles.header} data-test-id="monogram-header">
              Monogram
            </div>
            <div className={styles.buttonContainer}>
              <Button
                isFullSize={true}
                onClick={confirmMonogram}
                type={ButtonType.IconOnlyRound}
                size={ButtonSize.Small}
                testId="monogram-panel-confirm-btn"
              >
                <div className={styles.buttonContent}>
                  <div className={styles.buttonIcon}>
                    <i className="susu-icons" translate="no">
                      arrow_left
                    </i>
                  </div>
                </div>
              </Button>
            </div>
            <div
              className={styles.listContainer}
              onScroll={e => {
                scrollUp.handleScroll(e, 82);
              }}
            >
              {monogramData?.positions &&
                Object.values(monogramData?.positions).map(position => {
                  if (
                    !state.hiddenOptionsAndValues ||
                    state.hiddenOptionsAndValues.monogramPositions.indexOf(position.id) < 0
                  ) {
                    const imageUrls = getImageUrl(
                      position.imageUrlTemplate,
                      position.imageName,
                      state.selectedFabric?.layerImageName!,
                    );

                    return (
                      <div
                        key={'position_' + position.id}
                        className={cn(styles.listItem, {
                          [styles.clickable]: !state.selectedMonogram[position.id],
                          [styles.hasMissingValues]: state.monogramsIdsWithMissingSelection?.includes(position.id),
                        })}
                        onClick={
                          !state.selectedMonogram[position.id]
                            ? () => {
                                openMonogramDetails(position.id);
                              }
                            : undefined
                        }
                        onKeyDown={
                          !state.selectedMonogram[position.id]
                            ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                                onEnter(e, () => {
                                  openMonogramDetails(position.id);
                                });
                              }
                            : undefined
                        }
                        data-test-id={`monogram-position-${position.id}-id`}
                      >
                        <div className={styles.imageContainer}>
                          <img
                            alt=""
                            src={
                              imageUrlBuilder({
                                imageUrls: [imageUrls.imageUrl],
                                imageDefinition: imageDefinition.thumbnail,
                                noTransformation: true,
                              }).src
                            }
                            onError={e => {
                              onImageFailed(
                                e,
                                imageUrlBuilder({
                                  imageUrls: [imageUrls.fallbackUrl],
                                  imageDefinition: imageDefinition.thumbnail,
                                  noTransformation: true,
                                }).src,
                              );
                            }}
                          />
                        </div>
                        <div className={styles.textContainer}>
                          <div className={styles.textHeader} data-test-id={`monogram-position-${position.id}-name`}>
                            {position.name}
                          </div>
                          {state.selectedMonogram[position.id] && (
                            <div
                              className={styles.textDescription}
                              data-test-id={`monogram-position-${position.id}-description`}
                            >
                              {state.selectedMonogram[position.id]?.initials} •{' '}
                              {monogramData.colors![state.selectedMonogram[position.id]?.colorId ?? 0]?.name || 'None'}{' '}
                              •{' '}
                              {monogramData.fontTypes![state.selectedMonogram[position.id]?.fontStyleId ?? 0]?.name ||
                                'None'}
                            </div>
                          )}
                        </div>
                        {!state.selectedMonogram[position.id] && (
                          <div className={styles.arrowContainer}>
                            <i className="susu-icons" translate="no">
                              arrow_right
                            </i>
                          </div>
                        )}
                        {state.selectedMonogram[position.id] && (
                          <div className={styles.actionButtonsContainer}>
                            <div
                              onClick={() => {
                                editMonogram(position);
                              }}
                              data-test-id={`monogram-position-${position.id}-edit`}
                            >
                              <i className="susu-icons" translate="no">
                                edit
                              </i>
                            </div>
                            <div
                              onClick={() => {
                                deleteMonogram(position);
                              }}
                              data-test-id={`monogram-position-${position.id}-delete`}
                            >
                              <i className="susu-icons" translate="no">
                                trash
                              </i>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                })}
            </div>
          </div>
        )}
      </Panel>

      <Modal
        isOpen={isDeleteMonogramModalOpen}
        title={`Remove ${
          monogramData?.positions && deleteMonogramItem && monogramData?.positions[deleteMonogramItem.id]?.name
        } monogram?`}
        description=""
        firstButtonText="Remove"
        firstButtonType={ButtonType.SecondaryAlert}
        secondButtonText="Cancel"
        secondButtonType={ButtonType.Tertiary}
        onFirstButtonClick={confirmDeleteMonogram}
        onSecondButtonClick={closeDeleteMonogramModal}
        onOverlayClick={closeDeleteMonogramModal}
        isFullSize={true}
        testId="monogram-remove-modal"
      />
    </>
  );
};
